/**
 * useDialerDerivedData — Vues derivees pures du dialer.
 *
 * 2026-06-04 (Fowler etape 6/x du decoupage) — Extrait du god-context
 * `DialerContext.tsx`. Toutes les vues sont des `useMemo` pures qui
 * combinent des donnees brutes (prospectQueue, allCalls, activeCalls)
 * + des reglages (selectedCustomerId, parallelCallsCount, maxCallAttempts).
 *
 * Responsabilite unique :
 *   "Calculer les vues derivees (filtres customer, indexations, stats)
 *    sans muter de state ni effectuer de side-effect."
 *
 * Le hook ne touche AUCUN state direct, ne fait AUCUN side-effect, c'est
 * une transformation pure des inputs en outputs. Testable trivialement.
 */

import { isInterruptedCall } from '@/services/dialer/dialRules';
import { useMemo, useCallback } from 'react';
import { CONNECTED_CALL_OUTCOMES } from '@revcall/shared';
// @ts-ignore — service JS legacy
import { isQueuedStatus } from '@/services/dialer/prospectStatus';
import type { ParallelCall, ProspectQueueItem } from '@/contexts/DialerDataContext';
import { withProjectedLastCalls } from '@/services/dialer/lastCallProjection';

const PROSPECT_UI_ACTIVE_STATUSES = [
  'dialing',
  'ringing',
  'ai_analyzing',
  'human_detected',
  'connected_to_agent',
];

// La liste vient du paquet partagé : elle décide du taux de décroché, et une
// copie locale aurait donné un chiffre différent de celui du serveur.
const CONNECTED_OUTCOMES = CONNECTED_CALL_OUTCOMES as readonly string[];

interface UseDialerDerivedDataArgs {
  prospectQueue: ProspectQueueItem[];
  allCalls: ParallelCall[];
  activeCalls: ParallelCall[];
  selectedCustomerId: string | null;
  parallelCallsCount: number;
  maxCallAttempts: number | 'unlimited';
  maxCallAttemptsPeriod: 'day' | 'week' | 'month' | 'ever';
}

export interface DialerDerivedData {
  filteredProspectQueue: ProspectQueueItem[];
  filteredAllCalls: ParallelCall[];
  prospectsBySource: Record<string, ProspectQueueItem[]>;
  sources: string[];
  latestCallByPhone: Record<string, ParallelCall>;
  prospectCallMap: Record<string, ParallelCall>;
  callerIdStats: Record<string, { total: number; connected: number }>;
  openFollowupsCount: number;
  stats: {
    queuedProspects: number;
    activeCalls: number;
    dialingCalls: number;
    aiAnalyzing: number;
    connectedCalls: number;
    availableAgents: number;
    followupsCount: number;
  };
  callCountByPhone: Record<string, number>;
  isProspectAtCallLimit: (phone: string) => boolean;
}

export function useDialerDerivedData({
  prospectQueue,
  allCalls,
  activeCalls,
  selectedCustomerId,
  parallelCallsCount,
  maxCallAttempts,
  maxCallAttemptsPeriod,
}: UseDialerDerivedDataArgs): DialerDerivedData {
  // ADR-024 phase 2 — filtre customer applique UNE FOIS ici.
  const filteredProspectQueue = useMemo(() => {
    if (!selectedCustomerId) return prospectQueue;
    return prospectQueue.filter(
      (p) => (p.customer_id || '') === selectedCustomerId,
    );
  }, [prospectQueue, selectedCustomerId]);

  const filteredAllCalls = useMemo(() => {
    if (!selectedCustomerId) return allCalls;
    return allCalls.filter((c) => (c.customer_id || '') === selectedCustomerId);
  }, [allCalls, selectedCustomerId]);

  const prospectsBySource = useMemo(() => {
    return filteredProspectQueue
      .filter((p) => p.status !== 'placeholder')
      .reduce<Record<string, ProspectQueueItem[]>>((acc, p) => {
        const source = p.source || 'Revcall Client Dashboard';
        if (!acc[source]) acc[source] = [];
        acc[source].push(p);
        return acc;
      }, {});
  }, [filteredProspectQueue]);

  const sources = useMemo(() => {
    const allSourceSet = new Set<string>();
    filteredProspectQueue.forEach((p) => {
      allSourceSet.add(p.source || 'Revcall Client Dashboard');
    });
    if (allSourceSet.size === 0) allSourceSet.add('Revcall Client Dashboard');
    return Array.from(allSourceSet);
  }, [filteredProspectQueue]);

  const latestCallByPhone = useMemo(() => {
    const source =
      filteredAllCalls && filteredAllCalls.length > 0 ? filteredAllCalls : allCalls;
    const map: Record<string, ParallelCall> = {};
    source.forEach((c) => {
      const key = c.prospect_phone;
      if (!key || isInterruptedCall(c)) return;
      const prev = map[key];
      if (
        !prev ||
        new Date(c.created_date || '') > new Date(prev.created_date || '')
      ) {
        map[key] = c;
      }
    });
    // ENG-5: the server's projection of each contact's full history fills
    // in what the loaded (capped) calls miss.
    return withProjectedLastCalls(map, filteredProspectQueue);
  }, [allCalls, filteredAllCalls, filteredProspectQueue]);

  const prospectCallMap = useMemo(() => {
    const active = activeCalls
      .filter(
        (c) =>
          c.prospect_phone &&
          PROSPECT_UI_ACTIVE_STATUSES.includes(c.call_status || ''),
      )
      .sort((a, b) => {
        const ta = new Date(a.started_at || a.created_date || 0).getTime();
        const tb = new Date(b.started_at || b.created_date || 0).getTime();
        return tb - ta;
      });
    const cap =
      Number.isFinite(parallelCallsCount) && parallelCallsCount > 0
        ? parallelCallsCount
        : active.length;
    const map: Record<string, ParallelCall> = {};
    active.slice(0, cap).forEach((call) => {
      if (call.prospect_phone) map[call.prospect_phone] = call;
    });
    return map;
  }, [activeCalls, parallelCallsCount]);

  const callerIdStats = useMemo(() => {
    const s: Record<string, { total: number; connected: number }> = {};
    allCalls.forEach((c) => {
      const num = (c as { from_number?: string }).from_number;
      if (!num) return;
      if (!s[num]) s[num] = { total: 0, connected: 0 };
      s[num].total++;
      if (CONNECTED_OUTCOMES.includes(c.call_outcome || '')) s[num].connected++;
    });
    return s;
  }, [allCalls]);

  const openFollowupsCount = useMemo(
    () =>
      filteredAllCalls.filter(
        (c) =>
          c.call_outcome === 'callback_requested' &&
          (c as { follow_up_at?: string }).follow_up_at &&
          !(c as { follow_up_completed?: boolean }).follow_up_completed,
      ).length,
    [filteredAllCalls],
  );


  const stats = useMemo(
    () => ({
      queuedProspects: filteredProspectQueue.filter((p) =>
        isQueuedStatus(p.status),
      ).length,
      activeCalls: activeCalls.length,
      dialingCalls: activeCalls.filter(
        (c) => c.call_status === 'dialing' || c.call_status === 'ringing',
      ).length,
      aiAnalyzing: activeCalls.filter((c) => c.call_status === 'ai_analyzing')
        .length,
      connectedCalls: activeCalls.filter(
        (c) => c.call_status === 'connected_to_agent',
      ).length,
      availableAgents: 0,
      followupsCount: openFollowupsCount,
    }),
    [filteredProspectQueue, activeCalls, openFollowupsCount],
  );

  const callCountByPhone = useMemo(() => {
    if (maxCallAttempts === 'unlimited' || !maxCallAttempts) return {};
    const now = new Date();
    const periodStart = new Date(now);
    if (maxCallAttemptsPeriod === 'ever') {
      periodStart.setTime(0);
    } else if (maxCallAttemptsPeriod === 'week') {
      periodStart.setDate(periodStart.getDate() - 7);
    } else if (maxCallAttemptsPeriod === 'month') {
      periodStart.setMonth(periodStart.getMonth() - 1);
    } else {
      periodStart.setDate(periodStart.getDate() - 1);
    }
    const map: Record<string, number> = {};
    allCalls.forEach((c) => {
      const key = c.prospect_phone;
      if (!key || isInterruptedCall(c)) return;
      if (new Date(c.created_date || '') >= periodStart) {
        map[key] = (map[key] || 0) + 1;
      }
    });
    return map;
  }, [allCalls, maxCallAttempts, maxCallAttemptsPeriod]);

  const isProspectAtCallLimit = useCallback(
    (phone: string) => {
      // 0 or empty also means no limit (an older build stored "unlimited" as 0).
      if (maxCallAttempts === 'unlimited' || !maxCallAttempts) return false;
      return (
        (callCountByPhone[phone] || 0) >= parseInt(String(maxCallAttempts), 10)
      );
    },
    [maxCallAttempts, callCountByPhone],
  );

  return {
    filteredProspectQueue,
    filteredAllCalls,
    prospectsBySource,
    sources,
    latestCallByPhone,
    prospectCallMap,
    callerIdStats,
    openFollowupsCount,
    stats,
    callCountByPhone,
    isProspectAtCallLimit,
  };
}

/**
 * Open callbacks whose date has passed at `now` (CEO-4 « Tâches en retard »).
 * A plain function, called when the count is shown: a memo keyed on the call
 * list would freeze "now" and miss callbacks that fall due while the page is
 * open.
 */
export function countOverdueFollowups(calls: ReadonlyArray<{ call_outcome?: string | null }>, now: number): number {
  let n = 0;
  for (const c of calls) {
    const at = (c as { follow_up_at?: string }).follow_up_at;
    if (c.call_outcome === 'callback_requested' && at && !(c as { follow_up_completed?: boolean }).follow_up_completed && new Date(at).getTime() < now) n += 1;
  }
  return n;
}
